import {
  COVERAGE_SECTION_TITLE,
  MAX_SECTION_ITEMS,
  MIN_7D_RUNS,
  MIN_24H_RUNS,
  MIN_30D_RUNS_FOR_DAILY_VIEW,
  MIN_30D_RUNS_FOR_WEEKLY_VIEW,
  MIN_FINISHED_RUNS_FOR_TREND,
  MIN_TREND_DELTA_POINTS,
  MS_PER_DAY,
  NO_OBVIOUS_DIG_ITEM,
  WORTH_DIGGING_SECTION_TITLE,
} from "./runsSummaryConstants"
import {
  formatCount,
  formatDeltaPoints,
  formatRate,
  getPreviousTimeframeLabel,
  pluralize,
} from "./runsSummaryFormat"
import type {
  ResolvedRunsSummaryTimeframe,
  RunsPageSummarySection,
  RunsSummaryBucket,
  RunsSummaryTrend,
  RunsSummaryWindowStats,
} from "./runsSummaryTypes"

function parseBucketTime(bucket: RunsSummaryBucket): number | null {
  const time = Date.parse(bucket.date)
  return Number.isFinite(time) ? time : null
}

export function getWindowBounds(referenceDate: Date, windowDays: number) {
  const endTimeMs = referenceDate.getTime()
  const startTimeMs = endTimeMs - windowDays * MS_PER_DAY
  return { startTimeMs, endTimeMs }
}

function filterRunsSummaryBuckets(
  buckets: RunsSummaryBucket[],
  startTimeMs: number,
  endTimeMs: number,
): RunsSummaryBucket[] {
  return buckets.filter((bucket) => {
    const time = parseBucketTime(bucket)
    return time !== null && time >= startTimeMs && time < endTimeMs
  })
}

export function calculateWindowStats(
  buckets: RunsSummaryBucket[],
  startTimeMs: number,
  endTimeMs: number,
): RunsSummaryWindowStats {
  const windowBuckets = filterRunsSummaryBuckets(
    buckets,
    startTimeMs,
    endTimeMs,
  )
  const totalRuns = windowBuckets.reduce(
    (sum, bucket) => sum + bucket.totalRuns,
    0,
  )
  const completedRuns = windowBuckets.reduce(
    (sum, bucket) => sum + bucket.completedRuns,
    0,
  )
  const inProgressRuns = windowBuckets.reduce(
    (sum, bucket) => sum + bucket.inProgressRuns,
    0,
  )
  const finishedRuns = Math.max(0, totalRuns - inProgressRuns)
  const unsuccessfulFinishedRuns = Math.max(0, finishedRuns - completedRuns)

  return {
    totalRuns,
    completedRuns,
    inProgressRuns,
    finishedRuns,
    unsuccessfulFinishedRuns,
    completionRate: finishedRuns > 0 ? completedRuns / finishedRuns : null,
  }
}

function getStatsForWindow(
  buckets: RunsSummaryBucket[],
  referenceDate: Date,
  windowDays: number,
) {
  const { startTimeMs, endTimeMs } = getWindowBounds(referenceDate, windowDays)
  return calculateWindowStats(buckets, startTimeMs, endTimeMs)
}

function isCompletionDeltaBelowThreshold(
  currentStats: RunsSummaryWindowStats,
  previousStats: RunsSummaryWindowStats,
): boolean {
  if (currentStats.finishedRuns === 0 || previousStats.finishedRuns === 0) {
    return true
  }

  const numerator = Math.abs(
    currentStats.completedRuns * previousStats.finishedRuns -
      previousStats.completedRuns * currentStats.finishedRuns,
  )
  const denominator = currentStats.finishedRuns * previousStats.finishedRuns

  return numerator * 100 < MIN_TREND_DELTA_POINTS * denominator
}

export function selectAdaptiveRunsSummaryTimeframe(
  buckets: RunsSummaryBucket[],
  referenceDate = new Date(),
): ResolvedRunsSummaryTimeframe {
  const last24h = getStatsForWindow(buckets, referenceDate, 1)
  const last7d = getStatsForWindow(buckets, referenceDate, 7)
  const last30d = getStatsForWindow(buckets, referenceDate, 30)

  if (
    last24h.totalRuns >= MIN_24H_RUNS ||
    (last30d.totalRuns >= MIN_30D_RUNS_FOR_DAILY_VIEW && last24h.totalRuns > 0)
  ) {
    return "24h"
  }

  if (
    last7d.totalRuns >= MIN_7D_RUNS ||
    (last30d.totalRuns >= MIN_30D_RUNS_FOR_WEEKLY_VIEW && last7d.totalRuns > 0)
  ) {
    return "7d"
  }

  return "30d"
}

export function buildAutoReason(
  timeframe: ResolvedRunsSummaryTimeframe,
): string {
  switch (timeframe) {
    case "24h":
      return "Auto selected 24 hours because recent usage supports a daily view."
    case "7d":
      return "Auto selected 7 days because recent usage is weekly rather than daily."
    case "30d":
      return "Auto selected 30 days because recent usage is sparse."
  }
}

function buildOutcomeClause(stats: RunsSummaryWindowStats): string {
  if (stats.finishedRuns === 0) {
    if (stats.inProgressRuns > 0) {
      return `${formatCount(stats.inProgressRuns)} ${pluralize(
        stats.inProgressRuns,
        "run is",
        "runs are",
      )} still in progress and none have finished yet`
    }
    return "none have finished yet"
  }

  const finishedRunsText = `${formatCount(stats.finishedRuns)} finished ${pluralize(
    stats.finishedRuns,
    "run",
  )}`

  if (stats.unsuccessfulFinishedRuns === 0) {
    return `${finishedRunsText} all completed`
  }

  if (stats.completedRuns === 0) {
    return `${finishedRunsText} finished unsuccessfully`
  }

  return (
    `${finishedRunsText} split ${formatCount(stats.completedRuns)} completed / ` +
    `${formatCount(stats.unsuccessfulFinishedRuns)} unsuccessful`
  )
}

export function buildTrend(
  currentStats: RunsSummaryWindowStats,
  previousStats: RunsSummaryWindowStats,
  timeframe: ResolvedRunsSummaryTimeframe,
): RunsSummaryTrend {
  const previousLabel = getPreviousTimeframeLabel(timeframe)

  if (
    currentStats.completionRate === null ||
    previousStats.completionRate === null ||
    currentStats.finishedRuns < MIN_FINISHED_RUNS_FOR_TREND ||
    previousStats.finishedRuns < MIN_FINISHED_RUNS_FOR_TREND
  ) {
    return {
      label: `Trend unavailable vs ${previousLabel}`,
      sentence: `There is not enough prior-window volume for a reliable trend against the ${previousLabel}.`,
      tone: "neutral",
    }
  }

  const delta = currentStats.completionRate - previousStats.completionRate
  if (isCompletionDeltaBelowThreshold(currentStats, previousStats)) {
    return {
      label: `Completion steady vs ${previousLabel}`,
      sentence: `Completion is steady vs the ${previousLabel} at ${formatRate(
        currentStats.completionRate,
      )}.`,
      tone: "neutral",
    }
  }

  const isImprovement = delta > 0
  const direction = isImprovement ? "up" : "down"
  return {
    label: `Completion ${direction} ${formatDeltaPoints(delta)} vs ${previousLabel}`,
    sentence:
      `Completion is ${direction} ${formatDeltaPoints(delta)} vs the ${previousLabel} ` +
      `(${formatRate(previousStats.completionRate)} to ${formatRate(
        currentStats.completionRate,
      )}).`,
    tone: isImprovement ? "positive" : "critical",
  }
}

export function buildSummaryText(
  stats: RunsSummaryWindowStats,
  trend: RunsSummaryTrend,
  timeframeLabel: string,
  detailLeadSentence: string | null,
): string {
  const normalizedTimeframe = timeframeLabel.toLowerCase()

  if (stats.totalRuns === 0) {
    return `No runs started in the ${normalizedTimeframe}. ${trend.sentence}`
  }

  const totalRunsText = `${formatCount(stats.totalRuns)} ${pluralize(
    stats.totalRuns,
    "run",
  )} started`
  const baseSummary = `In the ${normalizedTimeframe}, ${totalRunsText}; ${buildOutcomeClause(
    stats,
  )}. ${trend.sentence}`
  return detailLeadSentence
    ? `${baseSummary} ${detailLeadSentence}`
    : baseSummary
}

export function buildRunOutcomeDigItems(
  stats: RunsSummaryWindowStats,
): string[] {
  const items: string[] = []
  if (stats.inProgressRuns > 0) {
    items.push(
      `${formatCount(stats.inProgressRuns)} ${pluralize(
        stats.inProgressRuns,
        "run is",
        "runs are",
      )} still in progress in this window; check whether ${pluralize(
        stats.inProgressRuns,
        "it is",
        "they are",
      )} expected long-running work or stale.`,
    )
  }
  if (stats.unsuccessfulFinishedRuns > 0) {
    items.push(
      `${formatCount(stats.unsuccessfulFinishedRuns)} finished ${pluralize(
        stats.unsuccessfulFinishedRuns,
        "run",
      )} did not complete successfully; open the failed runs before treating completion as healthy.`,
    )
  }
  return items
}

export function shouldPrioritizeRunOutcomes(
  stats: RunsSummaryWindowStats,
): boolean {
  return (
    stats.finishedRuns > 0 &&
    stats.unsuccessfulFinishedRuns * 2 > stats.finishedRuns
  )
}

export function mergeWorthDiggingItems(
  sections: RunsPageSummarySection[],
  runOutcomeItems: string[],
  prioritizeRunOutcomes: boolean,
): RunsPageSummarySection[] {
  if (runOutcomeItems.length === 0) {
    return sections
  }

  const mergedSections = sections.map((section) => ({ ...section }))
  const worthDiggingIndex = mergedSections.findIndex(
    (section) => section.title === WORTH_DIGGING_SECTION_TITLE,
  )
  const existingItems =
    worthDiggingIndex >= 0
      ? mergedSections[worthDiggingIndex].items.filter(
          (item) => item !== NO_OBVIOUS_DIG_ITEM,
        )
      : []
  const orderedItems = prioritizeRunOutcomes
    ? [...runOutcomeItems, ...existingItems]
    : [...existingItems, ...runOutcomeItems]
  const mergedItems = orderedItems.filter(
    (item, index, items) => items.indexOf(item) === index,
  )
  const worthDiggingSection = {
    title: WORTH_DIGGING_SECTION_TITLE,
    items: mergedItems.slice(0, MAX_SECTION_ITEMS),
  }

  if (worthDiggingIndex >= 0) {
    mergedSections[worthDiggingIndex] = worthDiggingSection
    return mergedSections
  }

  const coverageIndex = mergedSections.findIndex(
    (section) => section.title === COVERAGE_SECTION_TITLE,
  )
  if (coverageIndex >= 0) {
    mergedSections.splice(coverageIndex, 0, worthDiggingSection)
    return mergedSections
  }

  return [...mergedSections, worthDiggingSection]
}
